// Layer 3 (Transformation): the OptiFlow workflow catalogue and each
// definition's runs at the replay cursor (komatsu.K19). Pure: every run fact is
// derived from a seed or recording the rest of the product already renders —
// cases, the K6 investigation runs, case ladders, the analytics replay frames,
// report instances and rule-set versions. No count or time is typed here.
//
// Every row counts its runs on one window, the cursor's production day (its
// 18:00 start → the cursor), named once in the catalogue meta. Last run reads
// the newest run at or before the cursor even when it predates the window.

import type { AnalysisReplay } from '@/shared/contracts/analytics/v1';
import type { Case } from '@/versions/v1/schema/case';
import type { ReportTemplate } from '@/versions/v1/schema/report';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type {
  WorkflowActor,
  WorkflowDefinition,
  WorkflowTrigger,
  WorkflowVerb,
} from '@/versions/v1/schema/workflow';
import { WORKFLOW_DEFINITIONS } from '@/versions/v1/shared/workflow-catalogue-seed';
import { REPORT_INSTANCES, REPORT_TEMPLATES } from '@/versions/v1/shared/bunker-hill-seed';
import { AI_PANEL_META } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { RULE_CLAIMS, RULE_SET_VERSIONS } from '@/versions/v1/shared/bunker-hill-rule-claims';
import { reportTemplateCarriesMoney } from '@/versions/v1/shared/money-access';
import { ladderRead } from '@/versions/v1/shared/ladder.transform';
import { roleShortLabel } from '@/versions/v1/shared/org';
import { addMinutes, formatCaseClock, formatCursorBound, formatDayMonth, productionDayOf } from '@/versions/v1/shared/clock.derive';
import { caseStageAt } from '@/versions/v1/shared/caseStage';
import { ADVISORY_OPEN_STAGES } from '@/versions/v1/shared/advisory-nav.transform';
import { flowCaseTimelines, type FlowCaseStageInput } from '@/versions/v1/modules/advisory/FlowLanding.transform';
import {
  EXCURSION_WORKFLOW_NAME,
  investigationRunStatusLabel,
  runStepCount,
  type InvestigationRunRow,
} from '@/versions/v1/modules/advisory/InvestigationRun.transform';
import { reportsTabForTemplate } from '@/versions/v1/modules/reports/Scene.transform';

export type WorkflowRunTone = 'success' | 'warning' | 'default';

/** One run as the runs panel lists it. `at` sorts; `atLabel` prints. */
export type WorkflowRunRow = {
  id: string;
  subject: string;
  subjectMeta?: string;
  at: string;
  atLabel: string;
  status: string;
  tone: WorkflowRunTone;
  detail: string;
  href: string;
  linkLabel: string;
  /** Runs of one subject (a case fixture's pulls) share a group; the panel shows the latest few per group. */
  group?: { key: string; label: string; meta?: string };
};

export type WorkflowRunFact = { label: string; value: string };

/** Per-definition headers for the runs table's id, subject and detail columns; a null subject drops the column. */
export type WorkflowRunColumns = { id: string; subject: string | null; detail: string };

export type WorkflowCatalogueRow = {
  id: string;
  name: string;
  verb: WorkflowVerb;
  actor: WorkflowActor;
  triggers: readonly { kind: string; label: string }[];
  writes: string;
  /** True while the recording the runs derive from is still loading: no count, no last run yet. */
  loading: boolean;
  /** The newest run's clock at or before the cursor (even before the window), or `null` when it has never run. */
  lastRunLabel: string | null;
  /** Runs inside the window (`workflowRunWindow`). */
  runCount: number;
  /** The window's runs, newest first. */
  runs: readonly WorkflowRunRow[];
  /** The run Last run names when the window has none (it predates the window); null otherwise. */
  earlierRun: WorkflowRunRow | null;
  runColumns: WorkflowRunColumns;
  /** The runs panel's facts line: read-only tools, the next scheduled run, what a sweep caught. */
  facts: readonly WorkflowRunFact[];
};

export type WorkflowCatalogueInput = {
  /** `/projects/<slug>` — every run links under it. */
  projectPath: string;
  cursor: SeriesTime;
  /** Seed cases plus the scenario's, as the Advisory surfaces read them. */
  cases: readonly Case[];
  investigationRuns: readonly InvestigationRunRow[];
  investigationTools: string | null;
  /** False until the committed K6 trace has loaded. */
  investigationReady: boolean;
  /** Committed replay frames by case fixture (ADR-0013). */
  framesByFixture: Readonly<Record<string, AnalysisReplay>>;
  /** False until every case fixture's frames have loaded. */
  framesReady: boolean;
  /** The reader's rail segments (`visibleProjectNav`); a row whose runs open elsewhere is left out. */
  railSegments: ReadonlySet<string>;
  /** Money-carrying reports are listed only to readers with `money:view`. */
  showMoney: boolean;
  /** The Advisory strip's case basis (`useAdvisoryFlowInput`), on which the rail badge counts open cases; null until its replay frames land. */
  advisoryFlow: FlowCaseStageInput | null;
};

/** The one window every row counts runs on: the cursor's production day, from its start to the cursor. */
export function workflowRunWindow(cursor: SeriesTime): { start: SeriesTime; end: SeriesTime } {
  return { start: productionDayOf(cursor).start, end: cursor };
}

/** `11 workflows · runs since 07 Jun 18:00` — the catalogue meta, the one place the window is named. */
export function catalogueMeta(count: number, cursor: SeriesTime): string {
  return `${count} workflow${count === 1 ? '' : 's'} · runs since ${formatCursorBound(workflowRunWindow(cursor).start)}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const TRIGGER_KIND_LABEL: Readonly<Record<WorkflowTrigger['kind'], string>> = {
  event: 'Event',
  schedule: 'Schedule',
  manual: 'Manual',
};

/** `08 Jun 08:00` (a seed display stamp) → `2026-06-08T08:00`, in the cursor's year. */
export function seedStampTime(stamp: string, cursor: SeriesTime): SeriesTime | null {
  const match = stamp.match(/^(\d{1,2}) (\w{3}) (\d{2}:\d{2})$/);
  if (!match) return null;
  const month = MONTHS.indexOf(match[2]!);
  if (month < 0) return null;
  const year = cursor.slice(0, 4);
  return `${year}-${String(month + 1).padStart(2, '0')}-${match[1]!.padStart(2, '0')}T${match[3]}` as SeriesTime;
}

/** Replay frame instants are UTC ISO (`2026-06-07T03:04:00Z`); plant time is the same clock (clock.derive). */
function frameTime(at: string): SeriesTime {
  return at.slice(0, 16) as SeriesTime;
}

function dated(stamp: string | undefined): stamp is string {
  return Boolean(stamp && stamp.includes('T'));
}

function newestFirst(rows: WorkflowRunRow[]): WorkflowRunRow[] {
  return rows.sort((a, b) => b.at.localeCompare(a.at) || b.id.localeCompare(a.id, undefined, { numeric: true }));
}

function uniqueCases(cases: readonly Case[]): Case[] {
  return [...new Map(cases.map((caseItem) => [caseItem.id, caseItem])).values()];
}

function casePath(projectPath: string, caseId: string): string {
  return `${projectPath}/advisory/case/${caseId}`;
}

function caseRaisedBy(caseItem: Case, cursor: SeriesTime): boolean {
  return dated(caseItem.raisedAt) && caseItem.raisedAt <= cursor;
}

function caseClosedBy(caseItem: Case, cursor: SeriesTime): boolean {
  return dated(caseItem.resolvedAt) && caseItem.resolvedAt! <= cursor;
}

/** `advisory.alarm-flag`: each case raised by the cursor is the flag one run wrote. */
function alarmFlagRuns(input: WorkflowCatalogueInput): WorkflowRunRow[] {
  return newestFirst(
    uniqueCases(input.cases)
      .filter((caseItem) => caseRaisedBy(caseItem, input.cursor))
      .map((caseItem) => ({
        id: caseItem.code ?? caseItem.id,
        subject: caseItem.title,
        subjectMeta: caseItem.circuit,
        at: caseItem.raisedAt,
        atLabel: formatCaseClock(caseItem.raisedAt, input.cursor),
        status: caseClosedBy(caseItem, input.cursor) ? 'Case closed' : 'Case open',
        tone: 'default' as const,
        detail: roleShortLabel(caseItem.assignedTo),
        href: casePath(input.projectPath, caseItem.id),
        linkLabel: 'open case',
      })),
  );
}

function investigationTone(status: InvestigationRunRow['status']): WorkflowRunTone {
  if (status === 'approved') return 'success';
  if (status === 'running') return 'warning';
  return 'default';
}

/**
 * `advisory.excursion-investigation`: the K15 runs list, each opening its trace,
 * read from the recorded runs only — the recording the case page replays plus
 * the session's started runs. The catalogue therefore counts the same set
 * Advisory's Diagnosis stage counts into its Agent chip (K19 r1b). A seeded
 * case that reached Diagnosis with no recording (GANGUE-NEW) is not a run of
 * this workflow: its proposed action is a seed row no catalogue workflow
 * produces, and it stays uncatalogued rather than attributed to this one.
 * `id` is each run's own (the recording's, or the id the session assigned it).
 */
function investigationRuns(input: WorkflowCatalogueInput): WorkflowRunRow[] {
  const titleById = new Map(input.cases.map((caseItem) => [caseItem.id, caseItem.title]));
  return newestFirst(input.investigationRuns.map((run) => ({
    id: run.id,
    subject: titleById.get(run.caseId) ?? run.caseCode,
    subjectMeta: run.caseCode,
    at: run.startedAt,
    atLabel: formatCaseClock(run.startedAt, input.cursor),
    status: investigationRunStatusLabel(run.status),
    tone: investigationTone(run.status),
    detail: runStepCount(run.steps),
    href: `${casePath(input.projectPath, run.caseId)}?run=${run.id}`,
    linkLabel: 'open trace',
  })));
}

/**
 * `advisory.ladder-clock`: one run per case on a TARP ladder, started when the
 * case was raised; it waits at the current tier until the tier's deadline and
 * ends when the case closes (ADR-0008).
 */
function ladderClockRuns(input: WorkflowCatalogueInput): WorkflowRunRow[] {
  const rows: WorkflowRunRow[] = [];
  for (const caseItem of uniqueCases(input.cases)) {
    if (!caseItem.ladder || !caseRaisedBy(caseItem, input.cursor)) continue;
    const read = ladderRead(caseItem, input.cursor);
    if (!read) continue;
    const closed = caseClosedBy(caseItem, input.cursor);
    const deadline = formatCaseClock(read.deadline, input.cursor);
    const escalatesTo = read.escalatesTo ? `${roleShortLabel(read.escalatesTo)} at ` : '';
    rows.push({
      id: caseItem.code ?? caseItem.id,
      subject: caseItem.title,
      subjectMeta: caseItem.circuit,
      at: caseItem.raisedAt,
      atLabel: formatCaseClock(caseItem.raisedAt, input.cursor),
      status: closed ? 'Ended' : read.overdue ? `Tier ${read.tierNumber} overdue` : `Waiting · tier ${read.tierNumber}`,
      tone: !closed && read.overdue ? 'warning' : 'default',
      detail: closed ? 'case closed' : `${escalatesTo}${deadline}`,
      href: casePath(input.projectPath, caseItem.id),
      linkLabel: 'open case',
    });
  }
  return newestFirst(rows);
}

/** `analytics.scheduled-pull`: every committed replay frame whose cutoff the cursor has passed. */
function analysisPullRuns(input: WorkflowCatalogueInput): WorkflowRunRow[] {
  const rows: WorkflowRunRow[] = [];
  for (const [fixture, frames] of Object.entries(input.framesByFixture)) {
    const caseItem = input.cases.find((row) => row.analysisRun?.fixture === fixture);
    if (!caseItem) continue;
    for (const frame of frames) {
      const at = frameTime(frame.at);
      if (at > input.cursor) continue;
      const excursions = frame.result.excursions?.length ?? 0;
      const status = frame.result.status;
      const confidence = frame.result.data_quality.confidence;
      rows.push({
        id: frame.result.run_id.slice(0, 8),
        subject: `${excursions} excursion${excursions === 1 ? '' : 's'}`,
        at,
        atLabel: formatCaseClock(at, input.cursor),
        status: status === 'completed' ? 'Evaluated' : status === 'completed_non_evaluable' ? 'Not evaluable' : 'Failed',
        tone: status === 'failed' ? 'warning' : 'default',
        detail: confidence === null || confidence === undefined ? 'unmeasured' : `${Math.round(confidence * 100)}% confidence`,
        href: casePath(input.projectPath, caseItem.id),
        linkLabel: 'open case',
        group: { key: caseItem.id, label: caseItem.code ?? caseItem.id, meta: caseItem.title },
      });
    }
  }
  return newestFirst(rows);
}

/**
 * `advisory.ladder-sweep`: a fixed schedule (`everyMinutes`), counted from the
 * start of the cursor's production day — the day the daily report covers.
 * Each run checks the ladder cases open at its slot and escalates any whose
 * tier deadline had passed; the seed holds none, so every run catches zero.
 */
function ladderSweepRuns(input: WorkflowCatalogueInput, everyMinutes: number): { runs: WorkflowRunRow[]; caught: number } {
  const ladderCases = uniqueCases(input.cases).filter((caseItem) => caseItem.ladder && dated(caseItem.raisedAt));
  const rows: WorkflowRunRow[] = [];
  let caught = 0;
  let ordinal = 0;
  for (let slot = workflowRunWindow(input.cursor).start; slot <= input.cursor; slot = addMinutes(slot, everyMinutes)) {
    const open = ladderCases.filter((caseItem) => caseItem.raisedAt <= slot && !caseClosedBy(caseItem, slot));
    const missed = open.filter((caseItem) => {
      const read = ladderRead(caseItem, slot);
      return read !== null && caseItem.ladder!.enteredAt <= slot && read.overdue;
    }).length;
    caught += missed;
    ordinal += 1;
    rows.push({
      id: `#${ordinal}`,
      subject: `${open.length} ladder case${open.length === 1 ? '' : 's'} checked`,
      at: slot,
      atLabel: formatCaseClock(slot, input.cursor),
      status: 'Completed',
      tone: 'default',
      detail: `${missed} caught`,
      href: `${input.projectPath}/advisory`,
      linkLabel: 'open advisory',
    });
  }
  return { runs: newestFirst(rows), caught };
}

/**
 * `overview.priority-actions`: a fixed auto-refresh schedule (`everyMinutes`)
 * from the start of the cursor's production day, plus one event run after
 * each alarm flag raised in the window (the definition's second trigger). The
 * newest slot at the demo cursor is the seeded generation
 * (`AI_PANEL_META.generatedAt`); the next refresh is the seed's
 * `nextRefreshAt` (the panel's facts line). Each run ranks the cases open at its instant, counted with
 * the rail badge's rule — the Advisory strip's case stages
 * (`flowCaseTimelines`) on `ADVISORY_OPEN_STAGES` — so the newest run and the
 * badge read one figure.
 */
function priorityRuns(
  input: WorkflowCatalogueInput,
  flow: FlowCaseStageInput,
  everyMinutes: number,
  flags: readonly WorkflowRunRow[],
): WorkflowRunRow[] {
  const timelines = [...flowCaseTimelines(flow).values()];
  const openAt = (at: SeriesTime) => timelines.filter((timeline) => {
    const stage = caseStageAt(at, timeline);
    return stage !== null && ADVISORY_OPEN_STAGES.includes(stage);
  }).length;
  const { start } = workflowRunWindow(input.cursor);
  const starts: { at: SeriesTime; subject: string; order: number }[] = [];
  for (let slot = start; slot <= input.cursor; slot = addMinutes(slot, everyMinutes)) {
    starts.push({ at: slot, subject: 'Schedule', order: 0 });
  }
  for (const flag of flags) {
    if (flag.at >= start && flag.at <= input.cursor) starts.push({ at: flag.at as SeriesTime, subject: `Event · ${flag.id} flagged`, order: 1 });
  }
  starts.sort((a, b) => a.at.localeCompare(b.at) || a.order - b.order);
  const rows = starts.map((run, index) => {
    const open = openAt(run.at);
    return {
      id: `#${index + 1}`,
      subject: run.subject,
      at: run.at,
      atLabel: formatCaseClock(run.at, input.cursor),
      status: 'Completed',
      tone: 'default' as const,
      detail: `${open} open case${open === 1 ? '' : 's'}`,
      href: `${input.projectPath}/advisory`,
      linkLabel: 'open advisory',
    };
  });
  return newestFirst(rows);
}

/** `08 Jun 2026 · Day 06:00–18:00 · 11:30` → `08 Jun · Day`; `Week to 08 Jun 2026` → `Week to 08 Jun`. */
export function reportPeriodLabel(period: string): string {
  const [date, shift] = period.replace(/ \d{4}\b/, '').split(' · ');
  const shiftWord = shift?.split(' ')[0];
  return shiftWord ? `${date} · ${shiftWord}` : date!;
}

function reportTemplate(definition: WorkflowDefinition): ReportTemplate | undefined {
  return REPORT_TEMPLATES.find((row) => row.id === definition.reportTemplateId);
}

/** `reports.*`: each report instance generated by the cursor is one run, opening that report. */
function reportRuns(template: ReportTemplate, input: WorkflowCatalogueInput): WorkflowRunRow[] {
  const tab = reportsTabForTemplate(template.id);
  const href = `${input.projectPath}/reports${tab ? `?tab=${tab}` : ''}`;
  const rows: WorkflowRunRow[] = [];
  for (const instance of REPORT_INSTANCES) {
    if (instance.templateId !== template.id) continue;
    const at = seedStampTime(instance.generatedAt, input.cursor);
    if (!at || at > input.cursor) continue;
    const delivered = instance.deliveredAt ? seedStampTime(instance.deliveredAt, input.cursor) : null;
    const deliveredByCursor = delivered !== null && delivered <= input.cursor;
    rows.push({
      id: reportPeriodLabel(instance.period),
      subject: template.title,
      at,
      atLabel: formatCaseClock(at, input.cursor),
      status: deliveredByCursor ? 'Delivered' : 'Generated',
      tone: deliveredByCursor ? 'success' : 'default',
      detail: template.channel === 'email' ? 'Email' : 'In-app',
      href,
      linkLabel: 'open report',
    });
  }
  return newestFirst(rows);
}

/**
 * `ingest.fmea-workbook`: a rule-set version is an ingest run when it added a
 * claim sourced from the FMEA workbook. Versions carry a date, not a clock.
 */
function ruleSetIngestRuns(input: WorkflowCatalogueInput): WorkflowRunRow[] {
  const workbookClaimIds = new Set(
    RULE_CLAIMS.filter((claim) => claim.source.tier === 'document' && claim.source.sourceKind === 'fmea').map((claim) => claim.id),
  );
  const cursorDate = input.cursor.slice(0, 10);
  const rows: WorkflowRunRow[] = [];
  for (const version of RULE_SET_VERSIONS) {
    const date = version.acceptedAt.slice(0, 10);
    if (date > cursorDate) continue;
    const previous = new Set(RULE_SET_VERSIONS.find((row) => row.id === version.supersedes)?.claimIds ?? []);
    const added = version.claimIds.filter((id) => !previous.has(id) && workbookClaimIds.has(id)).length;
    if (added === 0) continue;
    rows.push({
      id: `v${version.n}`,
      subject: version.acceptedBy.name ?? roleShortLabel(version.acceptedBy.role),
      at: `${date}T00:00`,
      atLabel: formatDayMonth(`${date}T00:00` as SeriesTime),
      status: 'Accepted',
      tone: 'default',
      detail: `${added} rule${added === 1 ? '' : 's'}`,
      href: `${input.projectPath}/advisory/rule-set`,
      linkLabel: 'open rule set',
    });
  }
  return newestFirst(rows);
}

/** A template cadence reads as the tail of `Schedule · …`: `Weekly` → `weekly`, `08:00 daily` unchanged. */
function cadenceLabel(cadence: string): string {
  return cadence.charAt(0).toLowerCase() + cadence.slice(1);
}

const CASE_COLUMNS: WorkflowRunColumns = { id: 'Case', subject: 'Title', detail: 'Routed to' };

/** Every catalogue definition the reader may see, with its runs in the window and its last run. */
export function workflowCatalogueRows(input: WorkflowCatalogueInput): WorkflowCatalogueRow[] {
  const rows: WorkflowCatalogueRow[] = [];
  const window = workflowRunWindow(input.cursor);
  const flags = alarmFlagRuns(input);
  for (const definition of WORKFLOW_DEFINITIONS) {
    let name = definition.name;
    let triggers: readonly WorkflowTrigger[] = definition.triggers;
    let runs: WorkflowRunRow[] = [];
    let runColumns: WorkflowRunColumns = CASE_COLUMNS;
    const facts: WorkflowRunFact[] = [];
    let loading = false;
    if (!input.railSegments.has(definition.runSegment)) continue;

    switch (definition.runs) {
      case 'cases':
        runs = flags;
        break;
      case 'investigation':
        name = EXCURSION_WORKFLOW_NAME;
        runs = investigationRuns(input);
        runColumns = { id: 'Run', subject: 'Case', detail: 'Steps' };
        loading = !input.investigationReady;
        if (input.investigationTools) facts.push({ label: 'Read-only tools', value: input.investigationTools });
        break;
      case 'ladder':
        runs = ladderClockRuns(input);
        runColumns = { id: 'Case', subject: 'Title', detail: 'Escalates' };
        break;
      case 'ladder-sweep': {
        const read = ladderSweepRuns(input, definition.everyMinutes ?? 15);
        runs = read.runs;
        runColumns = { id: 'Run', subject: 'Checked', detail: 'Caught' };
        facts.push({ label: 'Missed deadlines caught', value: String(read.caught) });
        break;
      }
      case 'analysis-frames':
        loading = !input.framesReady;
        runs = analysisPullRuns(input);
        runColumns = { id: 'Run', subject: 'Found', detail: 'Data quality' };
        break;
      case 'priority-refresh': {
        loading = input.advisoryFlow === null;
        if (input.advisoryFlow) runs = priorityRuns(input, input.advisoryFlow, definition.everyMinutes ?? 30, flags);
        facts.push({ label: 'Next refresh', value: AI_PANEL_META.nextRefreshAt });
        runColumns = { id: 'Run', subject: 'Trigger', detail: 'Ranked' };
        break;
      }
      case 'report': {
        const template = reportTemplate(definition);
        if (!template) continue;
        if (!input.showMoney && reportTemplateCarriesMoney(template)) continue;
        name = template.title;
        triggers = [{ kind: 'schedule', label: cadenceLabel(template.cadence) }];
        runs = reportRuns(template, input);
        // The panel shows one report, titled by it: no Report column.
        runColumns = { id: 'Period', subject: null, detail: 'Channel' };
        if (template.nextSend) facts.push({ label: 'Next send', value: template.nextSend });
        break;
      }
      case 'rule-set':
        runs = ruleSetIngestRuns(input);
        runColumns = { id: 'Rule set', subject: 'Accepted by', detail: 'Workbook rules' };
        break;
    }

    const inWindow = runs.filter((run) => run.at >= window.start && run.at <= window.end);
    const last = runs.find((run) => run.at <= window.end) ?? null;
    rows.push({
      id: definition.id,
      name,
      verb: definition.verb,
      actor: definition.actor,
      triggers: triggers.map((trigger) => ({ kind: TRIGGER_KIND_LABEL[trigger.kind], label: trigger.label })),
      writes: definition.writes,
      loading,
      lastRunLabel: loading ? null : last?.atLabel ?? null,
      runCount: loading ? 0 : inWindow.length,
      runs: loading ? [] : inWindow,
      earlierRun: loading || inWindow.length > 0 ? null : last,
      runColumns,
      facts,
    });
  }
  return rows;
}

/** `76 runs` — a run group's count (the catalogue meta names the window). */
export function runCountLine(count: number): string {
  return `${count} run${count === 1 ? '' : 's'}`;
}

/** How many of a group's newest runs show before the rest fold into a disclosure. */
export const RUNS_SHOWN_PER_GROUP = 3;
/** The same fold for a definition whose runs are not grouped. */
export const RUNS_SHOWN_UNGROUPED = 6;

export type WorkflowRunGroup = {
  key: string;
  label?: string;
  meta?: string;
  count: number;
  latest: readonly WorkflowRunRow[];
  earlier: readonly WorkflowRunRow[];
};

/** Runs by group, newest group first, each split into what shows and what folds. */
export function workflowRunGroups(runs: readonly WorkflowRunRow[]): WorkflowRunGroup[] {
  const groups = new Map<string, { label?: string; meta?: string; rows: WorkflowRunRow[] }>();
  for (const run of runs) {
    const key = run.group?.key ?? '';
    const entry = groups.get(key) ?? { label: run.group?.label, meta: run.group?.meta, rows: [] };
    entry.rows.push(run);
    groups.set(key, entry);
  }
  return [...groups.entries()].map(([key, entry]) => {
    const shown = key ? RUNS_SHOWN_PER_GROUP : RUNS_SHOWN_UNGROUPED;
    return {
      key,
      label: entry.label,
      meta: entry.meta,
      count: entry.rows.length,
      latest: entry.rows.slice(0, shown),
      earlier: entry.rows.slice(shown),
    };
  });
}

/** `12 earlier runs` — the disclosure's summary. */
export function earlierRunsLabel(count: number): string {
  return `${count} earlier run${count === 1 ? '' : 's'}`;
}

/** `Before this window` — the group line over the one earlier run a zero-run window lists. */
export const EARLIER_RUN_GROUP_LABEL = 'Before this window';

/**
 * The group line a runs panel draws over its table: the run's own group label,
 * or `Before this window` when the window holds no run and the panel lists the
 * earlier run Last run names, so a table that predates the window is never read
 * as that window's runs. Null when the panel lists window runs.
 */
export function runsGroupLabel(row: Pick<WorkflowCatalogueRow, 'runs' | 'earlierRun'>): string | null {
  return row.runs.length === 0 && row.earlierRun ? EARLIER_RUN_GROUP_LABEL : null;
}
